import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useUpdateLead } from '../../../services/loanService';
import { LoanLead } from '../../../services/schemas/loanServiceSchemas';
import LeadForm, { LeadFormValues, leadToFormValues, toLeadPayload } from '../../modals/LeadForm';

interface EditLeadModalProps {
    /** The lead to edit; the modal is shown while this is set. */
    lead: LoanLead | null;
    onHide: () => void;
    onSaved?: (lead: LoanLead) => void;
}

/**
 * Fill in or correct what was captured on a lead, before it is converted. Only NEW leads can be
 * edited; the backend refuses the rest.
 */
const EditLeadModal: React.FC<EditLeadModalProps> = ({ lead, onHide, onSaved }) => {
    const queryClient = useQueryClient();
    const user = useSelector((state: RootState) => state.auth.user);
    const [error, setError] = useState<string | undefined>();

    const updateLead = useUpdateLead({
        mutation: {
            onSuccess: (saved) => {
                queryClient.invalidateQueries({ queryKey: ['/loan/api/v1/leads'] });
                queryClient.invalidateQueries({ queryKey: ['/loan/api/v1/leads/search'] });
                setError(undefined);
                onSaved?.(saved);
                onHide();
            },
            onError: (err: any) => {
                setError(err?.response?.data?.message || err?.message || 'The lead could not be saved.');
            }
        }
    });

    const handleHide = () => {
        if (updateLead.isPending) return;
        setError(undefined);
        onHide();
    };

    const onSubmit = (values: LeadFormValues) => {
        if (!lead?.id) return;
        setError(undefined);
        // Recorded as the lead's last editor, instead of the login id.
        updateLead.mutate({ id: lead.id, data: { ...toLeadPayload(values, lead), recordedBy: user?.name || user?.username } });
    };

    const title = lead?.registeredName
        || `${lead?.firstName || ''} ${lead?.lastName || ''}`.trim()
        || lead?.customerReference;

    return (
        <Modal show={!!lead} onHide={handleHide} size="lg" scrollable backdrop={updateLead.isPending ? 'static' : true}>
            <Modal.Header closeButton={!updateLead.isPending} style={{ backgroundColor: '#f8f9fa' }}>
                <Modal.Title className="fw-bold d-flex align-items-center">
                    <i className="fa fa-pencil text-primary me-2"></i>
                    Edit Lead
                    {title && <span className="text-muted fw-normal fs-6 ms-2">{title}</span>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
                {lead && (
                    <LeadForm
                        key={lead.id}
                        mode="edit"
                        defaultValues={leadToFormValues(lead)}
                        isPending={updateLead.isPending}
                        errorMessage={error}
                        onSubmit={onSubmit}
                        onCancel={handleHide}
                    />
                )}
            </Modal.Body>
        </Modal>
    );
};

export default EditLeadModal;
